import { useCallback, useEffect, useState } from "react"
import { useRecoilState, useRecoilValue } from "recoil"
import { useFetcher } from "src/hooks/useFetcher"
import { useRequestData } from "src/recoil/hooks/useRequestData"
import { deviceTokenState } from "src/recoil/atoms/account/deviceTokenState"
import { deviceTokenOptInBooleanState } from "src/recoil/atoms/account/deviceTokenOptInState"
import { getDeviceInfo, updateDeviceOptOut } from "src/aws/customAPI"
import { CustomAlert } from "src/utils/CustomAlert"

export const useUserDevicePushSettingData = () => {
  const [optInState, setOptInState] = useRecoilState(deviceTokenOptInBooleanState)
  const deviceToken = useRecoilValue(deviceTokenState)
  const requestDataResult = useRequestData()
  const [isInitialLoading, setIsInitialLoading] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const isLoading = isInitialLoading

  useEffect(() => {
    if (hasFetched) {
      setIsInitialLoading(false)
      return
    }

    if (!requestDataResult.isOk || !deviceToken) {
      setIsInitialLoading(false)
      return
    }

    const fetchInitialState = async () => {
      try {
        const accessToken = requestDataResult.content.accessToken
        const requestParams = { input: { id: deviceToken || "" } }
        const deviceInfo = await getDeviceInfo(accessToken, requestParams)

        if (deviceInfo.errors) {
          console.error("[PushSetting Debug] GraphQL errors:", deviceInfo.errors)
        }

        if (!deviceInfo.data?.getDeviceInfo) {
          setOptInState(true)
          return
        }

        const optOut = deviceInfo.data.getDeviceInfo.optOut

        if (optOut !== null && optOut !== undefined) {
          const newOptIn = !optOut
          setOptInState(newOptIn)
        } else {
          setOptInState(true)
        }
      } catch (error) {
        console.error("Failed to fetch initial optIn state:", error)
        setOptInState(true)
      } finally {
        setIsInitialLoading(false)
        setHasFetched(true)
      }
    }
    fetchInitialState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceToken, requestDataResult.isOk])

  const update = useCallback(
    async (params: { optIn: boolean | undefined }) => {
      if (!requestDataResult.isOk || !deviceToken) {
        return
      }

      const { accessToken } = requestDataResult.content
      const { optIn } = params

      if (optIn !== undefined) {
        setOptInState(optIn)
      }

      try {
        const requestParams = {
          input: {
            deviceToken: deviceToken,
            optOut: optIn !== undefined ? !optIn : undefined,
          },
        }

        const result = await updateDeviceOptOut(accessToken, requestParams)
        if (!result || result.errors) {
          if (optIn !== undefined) {
            setOptInState(!optIn)
          }
          await CustomAlert.alert("エラー", "通知設定の更新に失敗しました")
        }
        return optIn
      } catch (error) {
        if (optIn !== undefined) {
          setOptInState(!optIn)
        }
        console.error("failed Update Device OptOut Status", error)
        await CustomAlert.alert("エラー", "通知設定の更新に失敗しました")
        throw error
      }
    },
    [deviceToken, requestDataResult, setOptInState]
  )
  const { fetch: updateNewOptIn, isFetching: isUpdatingNewOptIn } = useFetcher(
    useCallback(
      async (next: boolean) => {
        await update({ optIn: next })
        return next
      },
      [update]
    )
  )

  return {
    isLoading,
    optInState,
    updateNewOptIn,
    isUpdatingNewOptIn,
  }
}
